"use client";

import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { cn } from "@/lib/utils";
import { Badge } from "@/shadcnComponents/ui/badge";
import { Card } from "@/shadcnComponents/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcnComponents/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";

interface CalendarProps {
  date?: Date;
  events?: Array<{
    id: string;
    title: string;
    type: string;
    date: string;
    selected: number;
    live: number;
    status: "active" | "scheduled" | "completed";
  }>;
}

export function Calendar({ date = new Date(), events = [] }: CalendarProps) {
  const firstDayOfMonth = startOfMonth(date);
  const lastDayOfMonth = endOfMonth(date);
  const startDate = startOfWeek(firstDayOfMonth);
  const endDate = endOfWeek(lastDayOfMonth);

  const days = eachDayOfInterval({ start: startDate, end: endDate });
  const weeks = Math.ceil(days.length / 7);

  const getDayEvents = (day: Date) =>
    events.filter((event) => isSameDay(new Date(event.date), day));
  const navigate = useNavigate();

  return (
    <div className="rounded-lg border bg-card">
      <div className="grid grid-cols-7 border-b text-center">
        {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((day) => (
          <div
            key={day}
            className="border-r p-4 text-sm font-medium last:border-r-0"
          >
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-rows-[repeat(6,_minmax(120px,_1fr))]">
        {Array.from({ length: weeks }).map((_, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7">
            {days.slice(weekIndex * 7, (weekIndex + 1) * 7).map((day) => {
              const dayEvents = getDayEvents(day);
              return (
                <div
                  key={day.toString()}
                  className={cn(
                    "relative border-r border-b p-2 last:border-r-0",
                    !isSameMonth(day, date) && "bg-muted"
                  )}
                >
                  <time
                    dateTime={format(day, "yyyy-MM-dd")}
                    className={cn(
                      "mb-2 block text-sm",
                      !isSameMonth(day, date) && "text-muted-foreground"
                    )}
                  >
                    {format(day, "d")}
                  </time>
                  <div className="space-y-1">
                    {dayEvents.map((event) => (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Card
                            key={event.id}
                            className={cn(
                              "p-2 text-xs cursor-pointer",
                              event.status === "active" && "bg-green-50",
                              event.status === "scheduled" && "bg-blue-50"
                            )}
                          >
                            <Badge
                              variant="outline"
                              className="mb-1 text-[10px]"
                            >
                              {event.type}
                            </Badge>
                            <div className="truncate">{event.title}</div>
                          </Card>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56">
                          <DropdownMenuLabel>
                            Campaign Details
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="flex flex-col items-start gap-1">
                            <span className="text-sm font-medium">
                              {event.title}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Selected: {event?.selected} influencers
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Live: {event?.live} influencers
                            </span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                `/Brand/DashBoard/AllCampaign/Campaign/${event?.id}`
                              )
                            }
                          >
                            View details
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
